import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Radio, TrendingDown, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RUNNER_BASELINE = 240;

const contracts = [
  { job: "Clinic Insulin Escort", sector: "Safe Zone B", category: "MEDICAL", payout: "1,200 CR", risk: 1 },
  { job: "Blood-Pack Courier Run", sector: "Sector 4 Trauma Vault", category: "MEDICAL", payout: "2,400 CR", risk: 2 },
  { job: "Ore Sample Relay", sector: "Dust Ridges", category: "MINING", payout: "3,800 CR", risk: 3 },
  { job: "Quantum-Battery Cell Ferry", sector: "Mine Shaft 9", category: "MINING", payout: "6,500 CR", risk: 4 },
  { job: "Water Ration Chip Delivery", sector: "Safe Zone A", category: "GOVERNMENT", payout: "8,900 CR", risk: 5 },
  { job: "Ledger Archive Transfer", sector: "New Atlanta Core", category: "GOVERNMENT", payout: "12,400 CR", risk: 6 },
  { job: "Ammunition Line Run", sector: "NADF Perimeter", category: "MILITARY", payout: "18,700 CR", risk: 7 },
  { job: "Radar Parts Crossing", sector: "Sector 7 Front", category: "MILITARY", payout: "26,300 CR", risk: 8 },
  { job: "Signal Dead-Drop Retrieval", sector: "Swarm Territory", category: "BLACK OPS", payout: "38,000 CR", risk: 9 },
  { job: "Unmarked Cargo — No Manifest", sector: "Night Window Only", category: "BLACK OPS", payout: "52,000 CR", risk: 10 },
];

export function CourierJobBoard() {
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);
  const [claimed, setClaimed] = useState(null);
  const [runners, setRunners] = useState(247);
  const [trend, setTrend] = useState(0);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/enlist/count`)
      .then(({ data }) => {
        if (active && typeof data.count === "number") setRunners(RUNNER_BASELINE + data.count);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      const delta = Math.floor(Math.random() * 7) - 3;
      if (delta !== 0) setTrend(delta > 0 ? 1 : -1);
      setRunners((prev) => Math.min(340, Math.max(180, prev + delta)));
    }, 2800);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let active = true;
    let clearTimer;
    const claimNext = () => {
      if (!active) return;
      const pick = contracts[Math.floor(Math.random() * contracts.length)];
      setClaimed(pick.job);
      clearTimer = window.setTimeout(() => {
        if (!active) return;
        setClaimed(null);
        window.setTimeout(claimNext, 3200 + Math.random() * 3800);
      }, 2400);
    };
    const startTimer = window.setTimeout(claimNext, 2600);
    return () => {
      active = false;
      window.clearTimeout(startTimer);
      window.clearTimeout(clearTimer);
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let raf;
    let last = 0;
    const step = (time) => {
      if (last && !pausedRef.current) {
        el.scrollTop += (time - last) * 0.035;
        const half = el.scrollHeight / 2;
        if (el.scrollTop >= half) el.scrollTop -= half;
      }
      last = time;
      raf = window.requestAnimationFrame(step);
    };
    raf = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  const pause = () => {
    pausedRef.current = true;
  };
  const resume = () => {
    pausedRef.current = false;
  };
  const resumeDelayed = () => {
    window.setTimeout(resume, 1600);
  };

  return (
    <section className="job-board" data-testid="courier-job-board-section">
      <Reveal className="job-board-inner">
        <div className="job-board-copy">
          <span>APEX-COURIER MK-VII // CONTRACT FEED</span>
          <h2>Low risk pays little. High risk pays everything.</h2>
          <p>
            Every Apex-Courier MK-VII terminal streams the live Wasteland Runner job board. Contracts rank from
            low-pay, low-risk medical errands to black-ops crossings where the payout matches the threat rating.
            Pick your contract. Keep your signal quiet. Get paid in full on arrival.
          </p>
          <Link to="/enlist" className="button button-primary" data-testid="job-board-enlist-link">
            ENLIST FOR CONTRACTS <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="job-board-terminal" data-testid="job-board-terminal">
          <div className="job-board-status">
            <span>
              <Radio size={13} /> LIVE CONTRACT FEED
            </span>
            <span className="job-board-runners" data-testid="runners-active-counter">
              RUNNERS ACTIVE
              <strong key={runners}>{runners}</strong>
              {trend > 0 ? <TrendingUp size={12} /> : trend < 0 ? <TrendingDown size={12} /> : null}
            </span>
            <span className="job-board-sort">SORTED: PAYOUT / RISK ASC</span>
          </div>
          <div className="job-board-head" aria-hidden="true">
            <span>CONTRACT / SECTOR</span>
            <span>DIVISION</span>
            <span>PAYOUT</span>
            <span>RISK</span>
          </div>
          <div
            className="job-board-scroll"
            data-testid="job-board-scroll"
            ref={scrollRef}
            onBlur={resume}
            onFocus={pause}
            onMouseEnter={pause}
            onMouseLeave={resume}
            onTouchEnd={resumeDelayed}
            onTouchStart={pause}
          >
            {[...contracts, ...contracts].map((contract, index) => (
              <div
                className={`job-row${claimed === contract.job ? " is-claimed" : ""}`}
                data-testid={index < contracts.length ? `job-row-${index + 1}` : undefined}
                key={`${contract.job}-${index}`}
              >
                {claimed === contract.job ? (
                  <span className="job-claimed" data-testid="job-claimed-stamp">
                    CLAIMED
                  </span>
                ) : null}
                <div className="job-row-main">
                  <strong>{contract.job}</strong>
                  <small>{contract.sector}</small>
                </div>
                <span className="job-cat">{contract.category}</span>
                <span className="job-payout">{contract.payout}</span>
                <span className="job-risk" aria-label={`Risk rating ${contract.risk} of 10`}>
                  <span className="job-risk-track">
                    <span className="job-risk-fill" style={{ width: `${contract.risk * 10}%` }} />
                  </span>
                  <small>{String(contract.risk).padStart(2, "0")}</small>
                </span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}
